import type { ReactNode } from "react";
import { Tooltip } from "@/components/ds";
import { toSeverityTone, toStateTone } from "@/lib/tone";
import { cn } from "@/lib/cn";

export type ThreatLevel = "low" | "medium" | "high" | "critical";
export type LinkState = "live" | "reconnecting" | "offline";

const THREAT_LABEL: Record<ThreatLevel, string> = { low: "Low", medium: "Medium", high: "High", critical: "Critical" };
const THREAT_BARS: Record<ThreatLevel, number> = { low: 1, medium: 2, high: 3, critical: 3 };

/**
 * The top bar's status group (DLP addition): one quiet, bordered group that
 * holds Live and the threat level, split by a hairline. Text stays neutral
 * and sentence case; colour is confined to the dot and the signal bars, and
 * comes from the tone vocabulary (data-tone → --tone), so it matches the
 * severity and state colours everywhere else.
 */
export function HeaderStatusBar({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div role="group" aria-label="System status" className={cn("rc-statusbar", className)}>
      {children}
    </div>
  );
}

/** Three bars, filled up to the level, in the level's severity colour. */
function SignalBars({ level }: { level: ThreatLevel }) {
  const filled = THREAT_BARS[level];
  return (
    <svg className="rc-statusbar__bars" viewBox="0 0 14 14" width={14} height={14} aria-hidden="true">
      {[0, 1, 2].map((i) => (
        <rect
          key={i}
          x={1 + i * 4.5}
          y={10 - i * 3.5}
          width={3}
          height={3 + i * 3.5}
          rx={1}
          data-on={i < filled ? "" : undefined}
        />
      ))}
    </svg>
  );
}

export interface ThreatLevelChipProps {
  level: ThreatLevel;
  /** Open alerts from the last 24 hours that set the level. */
  openAlerts: number;
  onClick?: () => void;
  className?: string;
}

/**
 * The threat level: the highest severity among the alerts still open from
 * the last 24 hours, or Low when there are none (the old bar's pill was
 * hard-coded to "low"). A button: it opens the Dashboard.
 */
export function ThreatLevelChip({ level, openAlerts, onClick, className }: ThreatLevelChipProps) {
  const detail =
    openAlerts === 0
      ? "No open alerts in the last 24 hours"
      : `${openAlerts} open alert${openAlerts === 1 ? "" : "s"} in the last 24 hours; the highest is ${THREAT_LABEL[level].toLowerCase()}`;
  return (
    <Tooltip content={detail}>
      <button
        type="button"
        onClick={onClick}
        aria-label={`Threat level: ${THREAT_LABEL[level]}. ${detail}`}
        data-tone={toSeverityTone(level)}
        data-level={level}
        className={cn("rc-statusbar__item rc-statusbar__item--button", className)}
      >
        <SignalBars level={level} />
        <span className="rc-statusbar__value">{THREAT_LABEL[level]}</span>
      </button>
    </Tooltip>
  );
}

const LINK: Record<LinkState, { state: string; label: string }> = {
  live: { state: "online", label: "Live" },
  reconnecting: { state: "stale", label: "Reconnecting" },
  offline: { state: "offline", label: "Offline" },
};

export interface LiveIndicatorProps {
  state: LinkState;
  /** When the console last heard from the API. */
  lastUpdated?: Date | null;
  className?: string;
}

/**
 * Whether the console is receiving live data (the old bar's "LIVE" pulsed
 * whatever the connection). Live while the top bar's poll is answered,
 * Reconnecting when a request gets no answer, Offline without a network.
 * Only Live pulses, and not under reduced motion.
 */
export function LiveIndicator({ state, lastUpdated, className }: LiveIndicatorProps) {
  const { state: tone, label } = LINK[state];
  const when = lastUpdated ? lastUpdated.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" }) : null;
  const detail =
    state === "live"
      ? `Receiving live data${when ? `; last update ${when}` : ""}`
      : state === "reconnecting"
        ? `The last refresh failed; retrying${when ? `. Last update ${when}` : ""}`
        : "No network connection";
  return (
    <Tooltip content={detail}>
      <span
        role="status"
        aria-label={`${label}. ${detail}`}
        tabIndex={0}
        data-state={state}
        className={cn("rc-statusbar__item", className)}
      >
        <span className="rc-statusbar__dot" data-tone={toStateTone(tone)} aria-hidden="true" />
        <span className="rc-statusbar__value">{label}</span>
      </span>
    </Tooltip>
  );
}
